import {Injectable, NgZone, inject} from '@angular/core';
import type {Analytics} from '@angular/fire/analytics';
import {FirebaseApp} from '@angular/fire/app';
import {environment} from '@/environments/environment';
import {CookieConsentService} from './cookie-consent.service';

/**
 * Encapsula Firebase Analytics con carga perezosa y respeto del
 * consentimiento de cookies (Declaración de Privacidad §2, opt-in).
 *
 * `main.ts` mantiene `provideFirebaseApp(...)` (Auth ya lo necesita) — pero
 * ese provider registra la app en el injector de Angular de forma perezosa:
 * solo se ejecuta `initializeApp()` cuando algo realmente inyecta
 * `FirebaseApp`. Por eso este servicio inyecta el token `FirebaseApp` (no
 * llama a `getApp()` de `firebase/app` directo) — así se garantiza que la
 * app por defecto exista antes de pedirle Analytics, en vez de asumirlo.
 *
 * @angular/fire/analytics sigue en package.json — no se desinstala, solo se
 * importa dinámicamente para que nunca quede en el bundle inicial ni se
 * ejecute antes del opt-in.
 */
@Injectable({providedIn: 'root'})
export class AnalyticsService {
    private readonly firebaseApp = inject(FirebaseApp);
    private readonly zone = inject(NgZone);
    private analytics?: Analytics;
    private syncing = false;

    constructor(private consent: CookieConsentService) {
        this.consent.changes$.subscribe(() => void this.sync());
    }

    /** Llamar una vez desde AppComponent. */
    init(): void {
        void this.sync();
    }

    /** No-op si Analytics no está cargado (sin consentimiento). */
    async logEvent(name: string, params?: Record<string, unknown>): Promise<void> {
        if (!this.analytics) {
            return;
        }
        const {logEvent} = await import('@angular/fire/analytics');
        logEvent(this.analytics, name, params);
    }

    private async sync(): Promise<void> {
        // Evita carreras si `changes$` emite dos veces seguidas antes de que
        // termine el import dinámico anterior.
        if (this.syncing) {
            return;
        }
        this.syncing = true;
        try {
            // Fuera de la zona de Angular: getAnalytics() descarga la config dinámica de Firebase con un
            // setTimeout de 60 s como tope de red. Dentro de la zona, ese timer pendiente mantenía
            // ApplicationRef.isStable en false durante un minuto en cada arranque con analytics aceptado, y
            // todo lo que espera esa estabilidad (p. ej. el botón "Actualizar" de resource-edit) se quedaba
            // sin funcionar. Analytics no toca la UI, así que no necesita disparar detección de cambios.
            await this.zone.runOutsideAngular(() => this.applyConsent());
        } finally {
            this.syncing = false;
        }
    }

    private async applyConsent(): Promise<void> {
        const granted = this.consent.granted('analytics') && !!environment.firebase?.measurementId;
        if (granted && !this.analytics) {
            const {getAnalytics, setAnalyticsCollectionEnabled} = await import(
                '@angular/fire/analytics'
            );
            this.analytics = getAnalytics(this.firebaseApp);
            setAnalyticsCollectionEnabled(this.analytics, true);
        } else if (!granted && this.analytics) {
            const {setAnalyticsCollectionEnabled} = await import('@angular/fire/analytics');
            setAnalyticsCollectionEnabled(this.analytics, false);
            this.clearGaCookies();
        }
    }

    /** Borra las cookies `_ga`/`_ga_*`/`_gid` al revocar el consentimiento. */
    private clearGaCookies(): void {
        try {
            const names = document.cookie
                .split(';')
                .map(c => c.trim().split('=')[0])
                .filter(name => name === '_ga' || name === '_gid' || name.startsWith('_ga_'));
            const domain = location.hostname.startsWith('www.')
                ? location.hostname.slice(3)
                : location.hostname;
            for (const name of names) {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${domain}`;
            }
        } catch {
            // document.cookie inaccesible (SSR / entorno restringido) — no bloqueante
        }
    }
}
